import { QUERY_KEYS } from "@/libraries/constant";
import { applyDataChanges } from "@/libraries/transform";
import { bulletinService } from "@/services";
import type {
	BulletinGroup,
	GetBulletinGroupsResponse,
	GetBulletinGroupsSubscribedResponse,
} from "@/types";
import type { QueryClient } from "@tanstack/react-query";

export const bulletinMutations = (queryClient: QueryClient) => {
	queryClient.setMutationDefaults([QUERY_KEYS.MUTATE_BULLETIN_SUBSCRIBE], {
		retry: 2,
		mutationFn: (bulletinIds: BulletinGroup["id"][]) =>
			bulletinService.createSubscribedGroup(bulletinIds),
		onMutate: async (bulletinIds) => {
			await queryClient.cancelQueries({
				queryKey: [QUERY_KEYS.GET_BULLETIN_SUBSCRIPTION_GROUPS],
			});
			await queryClient.cancelQueries({
				queryKey: [QUERY_KEYS.GET_BULLETIN_GROUPS],
			});
			const prevBulletinGroups =
				queryClient.getQueryData<GetBulletinGroupsResponse>([
					QUERY_KEYS.GET_BULLETIN_GROUPS,
				]);
			if (!prevBulletinGroups) return;
			const mergedGroups = prevBulletinGroups.my_groups.concat(
				prevBulletinGroups.publics,
			);
			const subscriptionGroups =
				queryClient.getQueryData<GetBulletinGroupsSubscribedResponse>([
					QUERY_KEYS.GET_BULLETIN_SUBSCRIPTION_GROUPS,
				]);
			const subscribedGroups = subscriptionGroups?.groups ?? [];
			const removedGroups =
				subscribedGroups.filter((group) => !bulletinIds.includes(group.id)) ??
				[];
			const addedGroups = mergedGroups.filter((group) =>
				bulletinIds.includes(group.id),
			);
			if (!addedGroups.length || !removedGroups.length) return;
			queryClient.setQueryData<GetBulletinGroupsSubscribedResponse>(
				[QUERY_KEYS.GET_BULLETIN_SUBSCRIPTION_GROUPS],
				(prevData) => ({
					...prevData,
					groups: applyDataChanges(
						prevData?.groups ?? [],
						addedGroups,
						removedGroups,
					),
				}),
			);
			return { addedGroups, removedGroups };
		},
		onError: (_error, _variables, context) => {
			if (!context?.addedGroups || !context?.removedGroups) return;
			queryClient.setQueryData<GetBulletinGroupsSubscribedResponse>(
				[QUERY_KEYS.GET_BULLETIN_SUBSCRIPTION_GROUPS],
				(prevData) => ({
					...prevData,
					groups: applyDataChanges(
						prevData?.groups ?? [],
						context.removedGroups,
						context.addedGroups,
					),
				}),
			);
		},
	});
};
